// @px7digital/system-libraries/auth-overlay — transports
//
// A transport is the bridge between <PX7AuthOverlay>'s UI and wherever
// credentials actually go. The component never talks to the network
// itself; it calls `transport.login(...)` / `transport.signup(...)` and
// renders whatever they resolve or reject with. That keeps the same
// overlay usable by two very different app shapes:
//
//   • SPA apps (localStorage JWT) → createAccountsTransport(): posts
//     straight to accounts.px7 over CORS via the accounts-client password
//     grant, storing the token client-side.
//
//   • BFF apps (server session cookie, e.g. GoDating) →
//     createBackendTransport(): posts to the app's OWN endpoints, which
//     broker the accounts call server-to-server and set their own
//     session cookie. The browser never holds a token.
//
// Contract — every transport exposes:
//   login({ email, password })  → Promise<result>   (reject on failure)
//   signup({ email, password }) → Promise<result>   (reject on failure)
//   reset?({ email })           → Promise<boolean>  (optional)
// Rejections should be Errors whose `.message` is safe to show the user;
// an optional `.code` lets the UI branch (e.g. "account_exists").

import {
  loginWithPassword,
  signupWithPassword,
  requestPasswordReset,
  AuthRequestError,
} from "../accounts-client/index.js";

export { AuthRequestError };

/**
 * Transport for SPA apps that hold the JWT client-side. Wraps the
 * accounts-client password grant — the token lands in localStorage under
 * the app namespace and `signed-in` is emitted.
 *
 * @param {{ app: string, accountsOrigin?: string }} options
 */
export function createAccountsTransport({ app, accountsOrigin } = {}) {
  if (!app) throw new Error("createAccountsTransport: missing app id");
  return {
    kind: "accounts",
    app,
    login: ({ email, password }) =>
      loginWithPassword({ app, email, password, accountsOrigin }),
    signup: ({ email, password }) =>
      signupWithPassword({ app, email, password, accountsOrigin }),
    reset: ({ email }) => requestPasswordReset({ email, accountsOrigin }),
  };
}

async function postJson(url, body) {
  let response;
  try {
    response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    throw new AuthRequestError(
      "Couldn't reach the server. Check your connection and try again.",
      "network",
    );
  }
  let data = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }
  if (!response.ok || !data || data.ok === false) {
    const message = (data && data.error) || "Something went wrong. Try again.";
    const code =
      (data && data.code) ||
      (response.status === 401
        ? "invalid_credentials"
        : response.status === 409
          ? "account_exists"
          : "request_failed");
    throw new AuthRequestError(message, code);
  }
  return data;
}

/**
 * Transport for BFF apps. Posts { email, password } to the app's own
 * endpoints, which broker the accounts login/signup server-side and set
 * the app's session cookie. The endpoints should return
 * `{ ok: true, ... }` on success or `{ ok: false, error }` on failure.
 *
 * @param {{ loginUrl: string, signupUrl?: string, resetUrl?: string }} options
 */
export function createBackendTransport({ loginUrl, signupUrl, resetUrl } = {}) {
  if (!loginUrl) throw new Error("createBackendTransport: missing loginUrl");
  return {
    kind: "backend",
    login: ({ email, password }) => postJson(loginUrl, { email, password }),
    signup: ({ email, password }) =>
      signupUrl
        ? postJson(signupUrl, { email, password })
        : Promise.reject(
            new AuthRequestError("Account creation isn't available here.", "unsupported"),
          ),
    reset: resetUrl
      ? ({ email }) =>
          postJson(resetUrl, { email })
            .then(() => true)
            .catch(() => false)
      : undefined,
  };
}
